



























import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Color, color, Root } from '@amcharts/amcharts5';
import { PictorialStackedSeries, SlicedChart } from '@amcharts/amcharts5/percent';

import { FeedTank, FeedTypeLevelMetricEnum, Sensor, Transceiver } from '@/api';
import { FeedTankCurrentState, feedTankSvgPath } from '../../../../models';
import { FeedTankService } from '../../../../services';

import Toast from '@/components/display/toast.vue';
import InformationRow from '@/components/display/information-row.vue';

@Component({
	components: {
		InformationRow,
	},
})
export default class FeedTankVolumeDisplay extends Vue {
	@Prop({ required: true, type: Object })
	private feedTank!: FeedTank;

	@Watch('feedTank')
	public async onFeedTankChanged(): Promise<void> {
		this.currentState = await this.fetchCurrentState();
		this.chartSeries?.data.setAll(this.formatChartSeriesData(this.currentState?.percentFull ?? 0));
	}

	public currentState: FeedTankCurrentState | null = null;
	public loading: boolean = true;

	private isVolume(): boolean {
		return this.feedTank.feedtype?.levelMetric === FeedTypeLevelMetricEnum.Volume;
	}

	private getUnit(isVolume: boolean, value?: number | null): string {
		return isVolume ? 'L' : (value ?? 0) > 1000 ? 't' : 'kg';
	}

	private convertToTonnes(isVolume: boolean, value?: number | null): string | null {
		return (
			(isVolume
				? value?.toString()
				: value != null
				? (value * (value >= 1000 ? 0.001 : 1)).toFixed(1)
				: null) ?? null
		);
	}

	public get currentVolumeString(): string {
		const isVolume = this.isVolume();
		const metric = isVolume ? this.currentState?.volume : this.currentState?.weight;
		if (metric != null) {
			return `${this.convertToTonnes(isVolume, metric)} ${this.getUnit(isVolume, metric)} - ${
				this.lastVolumeTime
			}`;
		}
		return 'Unknown';
	}

	public get lastVolumeTime(): string {
		if (this.currentState?.volumeTs) {
			return Vue.filter('dateTimeDiff')(this.currentState.volumeTs * 1000);
		}
		return 'Unknown';
	}

	public getEstimatedEmptyString(shortLong: 'long' | 'short'): string {
		const key = shortLong === 'short' ? 'shortEstimatedEmptyDate' : 'longEstimatedEmptyDate';
		const estimatedEmptyTs = this.currentState?.[key];
		if (typeof estimatedEmptyTs === 'number') {
			const date = new Date(estimatedEmptyTs * 1000);
			return `${Vue.filter('dateTimeDiff')(date)} - ${Vue.filter('shortDate')(date)}`;
		}
		return 'Unknown';
	}

	public getDailUsageString(shortLong: 'long' | 'short'): string {
		let usageKey = shortLong === 'short' ? 'shortDailyUse' : 'longDailyUse';
		const isVolume = this.isVolume();
		if (!isVolume) usageKey += 'Weight';
		const usageValue =
			<number | null>this.currentState?.[usageKey as keyof FeedTankCurrentState] ?? null;
		if (usageValue != null) {
			return `${this.convertToTonnes(isVolume, usageValue)} ${this.getUnit(
				isVolume,
				usageValue,
			)} per day `;
		}
		return 'Unknown';
	}

	public getLastRefillString(): string {
		const lastDeliveryTs = this.currentState?.lastDeliveryTimestamp;
		let lastDeliveryVol = this.currentState?.lastDeliveryVolume;
		const isVolume = this.isVolume();
		if (!isVolume) lastDeliveryVol = this.currentState?.lastDeliveryWeight;
		if (typeof lastDeliveryTs === 'number' && typeof lastDeliveryVol === 'number') {
			const date = new Date(lastDeliveryTs * 1000);
			return `${this.convertToTonnes(isVolume, lastDeliveryVol)} ${this.getUnit(
				isVolume,
				lastDeliveryVol,
			)} - ${Vue.filter('shortDate')(date)}`;
		}
		return 'Unknown';
	}

	private feedTankService: FeedTankService = new FeedTankService(this.feedTank);
	private toast: Toast = new Toast();
	private chartSeries: PictorialStackedSeries | null = null;

	public async created(): Promise<void> {
		this.currentState = await this.fetchCurrentState();
		this.chartSeries = this.renderChart();
		setTimeout(() => (this.loading = false), 500);
	}

	private async fetchCurrentState(): Promise<FeedTankCurrentState | null> {
		const currentStateResponse = await this.feedTankService.getCurrentState(this.feedTank);
		if (currentStateResponse?.errorMessage) {
			this.toast.makeToast(currentStateResponse.errorMessage, 'Error', 'danger');
			return null;
		}
		return currentStateResponse.data ?? {};
	}

	private renderChart(): PictorialStackedSeries {
		const root = Root.new('feedTankVolume');
		const chart = root.container.children.push(
			SlicedChart.new(root, {
				paddingTop: 20,
				paddingBottom: 0,
				layout: root.verticalLayout,
			}),
		);
		const series = chart.series.push(
			PictorialStackedSeries.new(root, {
				orientation: 'vertical',
				valueField: 'value',
				categoryField: 'name',
				startLocation: 0.583, // Roughly the length of the svg silo's legs
				endLocation: 0,
				svgPath: feedTankSvgPath,
				alignLabels: false,
			}),
		);
		series.slices.template.setAll({
			templateField: 'sliceSettings',
		});
		series.labels.template.set('forceHidden', true);
		series.data.setAll(this.formatChartSeriesData(this.currentState?.percentFull ?? 0));
		chart.appear();
		return series;
	}

	private formatChartSeriesData(
		percentFull: number,
	): Array<{
		name: string;
		sliceSettings: {
			fill: Color;
			stroke: Color;
			showTooltipOn?: 'hover' | 'always';
			tooltipText?: string;
		};
		value: number;
	}> {
		return [
			{
				name: 'Volume',
				value: percentFull,
				sliceSettings: {
					fill: color(0x3395b1),
					stroke: color(0x3395b1),
					showTooltipOn: 'always',
				},
			},
			{
				name: 'Empty',
				value: 100 - percentFull,
				sliceSettings: {
					fill: color(0xffffff),
					stroke: color(0xffffff),
					tooltipText: '',
				},
			},
		];
	}
}
